import { Link } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import {
  ShippingDocumentLineRowFragment,
  ShippingDocumentLinesQuery,
  ShippingDocumentLinesQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2, useLocalOmnisearch } from "hooks/omnisearch-v2";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { buildWasteStreamUrl } from "util/wasteUrls";

import { SHIPPING_DOCUMENT_LINES_QUERY } from "./api";
import { DECLARED_WASTE_KIND_LABELS } from "./extractionReview";
import { NO_VALUE_PLACEHOLDER } from "./shippingDocumentDisplay";

type LineRow = ShippingDocumentLineRowFragment;

/** Comma-joined codes, or the shared placeholder when there are none. */
function codesText(codes: readonly string[]): string {
  return codes.length > 0 ? codes.join(", ") : NO_VALUE_PLACEHOLDER;
}

function buildColumns(tenantId: string): OmnisearchGridColDef<LineRow>[] {
  return [
    {
      field: "description",
      headerName: "Description",
      flex: 1.2,
      minWidth: 220,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => row.description ?? NO_VALUE_PLACEHOLDER,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) =>
        row.quantity == null
          ? NO_VALUE_PLACEHOLDER
          : `${row.quantity}${row.unit ? ` ${row.unit}` : ""}`,
    },
    {
      field: "declaredWasteKind",
      headerName: "Declared kind",
      flex: 0.6,
      minWidth: 130,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) =>
        row.declaredWasteKind
          ? DECLARED_WASTE_KIND_LABELS[row.declaredWasteKind]
          : NO_VALUE_PLACEHOLDER,
    },
    {
      // What the line's description and federal codes imply, computed by the
      // engine on every read rather than stored. Not sortable: there is no
      // column behind it to sort on.
      field: "suggestedWasteKind",
      headerName: "Suggested kind",
      flex: 0.6,
      minWidth: 130,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => DECLARED_WASTE_KIND_LABELS[row.suggestedWasteKind],
    },
    {
      field: "federalWasteCodes",
      headerName: "Federal codes",
      flex: 0.6,
      minWidth: 130,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => codesText(row.federalWasteCodes),
    },
    {
      field: "stateWasteCodes",
      headerName: "State codes",
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => codesText(row.stateWasteCodes),
    },
    {
      field: "wasteStream",
      headerName: "Stream",
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      filterable: false,
      // The matched record is the useful thing to reach from a line, since a
      // line itself has no detail of its own beyond what this row shows.
      renderCell: ({ row }) =>
        row.wasteStream ? (
          <Link
            component={RouterLink}
            to={buildWasteStreamUrl(tenantId, row.wasteStream.id)}
            onClick={(event) => event.stopPropagation()}
          >
            {row.wasteStream.name}
          </Link>
        ) : (
          NO_VALUE_PLACEHOLDER
        ),
    },
    {
      field: "container",
      headerName: "Container",
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) =>
        row.container ? (
          <Link
            component={RouterLink}
            to={`/o/${tenantId}/waste/containers/${row.container.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {row.container.containerLabel}
          </Link>
        ) : (
          NO_VALUE_PLACEHOLDER
        ),
    },
  ];
}

/** Document order, which is how the reviewer saved them and how the paper reads. */
const INITIAL_SORT: GridSortModel = [{ field: "position", sort: "asc" }];

const getItems = (data: ShippingDocumentLinesQuery) =>
  data.wasteShippingDocument?.linesResult?.items ?? [];
const getCount = (data: ShippingDocumentLinesQuery) =>
  data.wasteShippingDocument?.linesResult?.count ?? 0;

const toApiSortModel = (sort: GridSortModel) => sort.map((s) => ({ field: s.field, sort: s.sort }));

/**
 * A shipping document's lines, in the same grid the manifest and waste-stream
 * surfaces use for their lines, so one interaction covers all three.
 *
 * Its own query rather than the lines already on the detail read: a grid pages
 * and sorts server-side, and reusing the nested list would mean refetching the
 * whole document to turn a page.
 */
export function ShippingDocumentLinesDataGrid({
  tenantId,
  shippingDocumentId,
}: {
  tenantId: string;
  shippingDocumentId: string;
}) {
  // This grid only ever renders inside the shipping-document drawer, over a
  // route that has its own omnisearch in the URL. Reading that URL would apply
  // the list's search term to the lines query and show it in the lines search
  // box, so the lines grid keeps its search in local state instead.
  const omnisearchSource = useLocalOmnisearch();

  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): ShippingDocumentLinesQueryVariables => ({
      id: shippingDocumentId,
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [shippingDocumentId]
  );

  return (
    <OmnisearchDataGridV2
      omnisearchSource={omnisearchSource}
      persistenceKey="shipping-document-lines-table"
      columns={buildColumns(tenantId)}
      query={SHIPPING_DOCUMENT_LINES_QUERY}
      getItems={getItems}
      getCount={getCount}
      buildVariables={buildVariables}
      noRowsMessage="This shipping document has no lines."
      initialSortModel={INITIAL_SORT}
      disableRowSelectionOnClick
      tenantId={tenantId}
      // The grid pads itself for use as a whole page. Nested in a card that
      // already has its own padding, that inset leaves the search box and the
      // columns sitting in from the section heading, and doubles the gap under
      // it. The card supplies both.
      sx={{ px: 0, pt: 0 }}
    />
  );
}
